import { useState, useEffect, useCallback } from "react";
import { apiFetch } from "@/context/AuthContext";
import { toast } from "sonner";
import { Loader2, Send, MessageSquare, ChevronDown, ChevronRight, Star } from "lucide-react";

const STATUS_COLOR = { open: "text-[#7aa2ff]", pending: "text-amber-300", waiting_customer: "text-purple-300", resolved: "text-emerald-300", closed: "text-slate-400" };

export default function MyTickets() {
  const [tickets, setTickets] = useState(null);
  const [open, setOpen] = useState(null);
  const [detail, setDetail] = useState(null);
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => { apiFetch("/tickets/mine").then(setTickets).catch(() => setTickets([])); }, []);
  useEffect(() => { load(); }, [load]);

  const toggle = async (id) => {
    if (open === id) { setOpen(null); setDetail(null); return; }
    setOpen(id); setDetail(null);
    try { setDetail(await apiFetch(`/tickets/${id}`)); } catch (e) { toast.error(e.message); }
  };

  const send = async () => {
    if (!reply.trim()) return;
    setBusy(true);
    try { await apiFetch(`/tickets/${open}/reply`, { method: "POST", body: { body: reply.trim() } });
      setReply(""); setDetail(await apiFetch(`/tickets/${open}`)); load(); toast.success("Reply sent");
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const rate = async (ticketId, rating) => {
    try { await apiFetch(`/tickets/${ticketId}/csat`, { method: "POST", body: { rating } });
      toast.success("Thanks for your feedback!"); setDetail(await apiFetch(`/tickets/${ticketId}`)); load();
    } catch (e) { toast.error(e.message); }
  };

  if (!tickets) return null;

  return (
    <>
      <h2 className="font-display font-bold text-xl mt-12 mb-4 flex items-center gap-2"><MessageSquare size={18} /> My support tickets</h2>
      <div className="rounded-2xl border border-white/10 bg-[#0a0a0a] divide-y divide-white/5" data-testid="my-tickets">
        {tickets.length === 0 && <p className="p-4 text-slate-500 text-sm">No tickets yet. Use the help button to reach support.</p>}
        {tickets.map((t) => (
          <div key={t.id}>
            <button onClick={() => toggle(t.id)} className="w-full flex items-center justify-between p-4 text-left hover:bg-white/5 transition-colors duration-200" data-testid={`my-ticket-${t.number}`}>
              <span className="flex items-center gap-2 text-sm text-white">{open === t.id ? <ChevronDown size={14} /> : <ChevronRight size={14} />} {t.subject}</span>
              <span className={`text-[11px] font-mono uppercase ${STATUS_COLOR[t.status]}`}>{t.status.replace("_", " ")}</span>
            </button>
            {open === t.id && (
              <div className="px-4 pb-4" data-testid={`my-ticket-detail-${t.number}`}>
                {!detail ? <Loader2 className="animate-spin text-slate-400" size={16} /> : (
                  <>
                    <div className="space-y-2 mb-3 max-h-64 overflow-auto">
                      {detail.messages.map((m) => (
                        <div key={m.id} className={`rounded-lg p-3 border text-sm ${m.author_type === "agent" ? "border-[#0055FF]/30 bg-[#0055FF]/5" : "border-white/10 bg-black/30"}`}>
                          <p className="text-[11px] text-slate-500 mb-1"><span className="text-white">{m.author_name}</span> · {m.author_type}</p>
                          <p className="text-slate-200 whitespace-pre-wrap">{m.body}</p>
                        </div>
                      ))}
                    </div>
                    {t.status !== "closed" && (
                      <div className="flex gap-2">
                        <input value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Reply…" className="flex-1 bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm outline-none focus:border-[#0055FF]" data-testid="my-ticket-reply-input" />
                        <button onClick={send} disabled={busy || !reply.trim()} className="inline-flex items-center gap-1.5 bg-[#0055FF] text-white text-sm font-semibold px-3 py-2 rounded-lg disabled:opacity-40" data-testid="my-ticket-reply-send">{busy ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />}</button>
                      </div>
                    )}
                    {["resolved", "closed"].includes(t.status) && (
                      <div className="mt-3 rounded-lg border border-white/10 bg-black/30 p-3" data-testid={`my-ticket-csat-${t.number}`}>
                        {detail.csat?.rating ? (
                          <p className="text-sm text-emerald-400">You rated this {detail.csat.rating}/5 ★ — thank you!</p>
                        ) : (
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-slate-300">How did we do?</span>
                            {[1, 2, 3, 4, 5].map((n) => (
                              <button key={n} onClick={() => rate(t.id, n)} className="text-slate-500 hover:text-amber-400 transition-colors duration-200" data-testid={`csat-star-${t.number}-${n}`}><Star size={18} /></button>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </>
  );
}
